import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit2, LayoutGrid, Lock, Plus, Settings2, Trash2, Save } from 'lucide-react';
import { useState } from 'react';

import type {
  CommissionRule,
  CreateCommissionRuleInput,
  UpdateCommissionRuleInput,
  GlobalSetting,
} from '@terra/api-client';
import { Badge, Button, EmptyState, Input, PageHeader, Spinner } from '@terra/ui';

import { api } from '../lib/api';
import {
  MODULES,
  MODULES_SETTING_KEY,
  isModuleOn,
  useModulesStore,
  type ModuleToggles,
} from '../lib/modules';

function CommissionRulesTab() {
  const qc = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);

  // Modal State
  const [scope, setScope] = useState<'GLOBAL' | 'CATEGORY' | 'VENDOR'>('GLOBAL');
  const [bps, setBps] = useState<number>(100);
  const [categoryId, setCategoryId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [effectiveTo, setEffectiveTo] = useState('');

  const { data: rules, isLoading } = useQuery({
    queryKey: ['admin-commission-rules'],
    queryFn: () => api.commissions.listRules().then((res) => res.data),
  });

  const createMut = useMutation({
    mutationFn: (data: CreateCommissionRuleInput) => api.commissions.createRule(data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-commission-rules'] });
      setIsModalOpen(false);
      resetModal();
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCommissionRuleInput }) =>
      api.commissions.updateRule(id, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-commission-rules'] });
      setEditingRuleId(null);
      setIsModalOpen(false);
      resetModal();
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.commissions.deleteRule(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-commission-rules'] });
    },
  });

  const resetModal = () => {
    setScope('GLOBAL');
    setBps(100);
    setCategoryId('');
    setVendorId('');
    setEffectiveFrom('');
    setEffectiveTo('');
    setEditingRuleId(null);
  };

  const handleOpenCreate = () => {
    resetModal();
    setIsModalOpen(true);
  };

  const handleOpenEdit = (r: CommissionRule) => {
    resetModal();
    setEditingRuleId(r.id);
    setScope(r.scope);
    setBps(r.bps);
    if (r.categoryId) setCategoryId(r.categoryId || '');
    if (r.vendorId) setVendorId(r.vendorId || '');
    if (r.effectiveTo) setEffectiveTo(new Date(r.effectiveTo).toISOString().split('T')[0] || '');
    setIsModalOpen(true);
  };

  const handleSave = () => {
    if (editingRuleId) {
      const updateData: UpdateCommissionRuleInput = { bps };
      if (effectiveTo) updateData.effectiveTo = new Date(effectiveTo).toISOString();
      updateMut.mutate({ id: editingRuleId, data: updateData });
    } else {
      const createData: CreateCommissionRuleInput = { scope, bps };
      if (scope === 'CATEGORY' && categoryId) createData.categoryId = categoryId;
      if (scope === 'VENDOR' && vendorId) createData.vendorId = vendorId;
      if (effectiveFrom) createData.effectiveFrom = new Date(effectiveFrom).toISOString();
      if (effectiveTo) createData.effectiveTo = new Date(effectiveTo).toISOString();

      createMut.mutate(createData);
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-ink text-lg font-semibold tracking-tight">Commission rules</h2>
        <Button onClick={handleOpenCreate} size="sm">
          <Plus className="mr-2 h-4 w-4" /> Create Rule
        </Button>
      </div>

      {isLoading ? (
        <div className="grid min-h-[100px] place-items-center">
          <Spinner size="lg" />
        </div>
      ) : (rules ?? []).length === 0 ? (
        <EmptyState
          title="No commission rules"
          description="Commission rules will appear here once created."
        />
      ) : (
        <div className="border-border bg-surface overflow-x-auto rounded-lg border">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-border bg-surface-subtle text-ink-muted border-b text-xs font-medium uppercase">
                <th className="px-4 py-3">Scope</th>
                <th className="px-4 py-3">Target</th>
                <th className="px-4 py-3">Rate (BPS)</th>
                <th className="px-4 py-3">Rate (%)</th>
                <th className="px-4 py-3">Effective</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(rules ?? []).map((r) => (
                <tr
                  key={r.id}
                  className="border-border hover:bg-surface-subtle/50 border-b transition-colors last:border-0"
                >
                  <td className="px-4 py-3">
                    <Badge
                      tone={
                        r.scope === 'GLOBAL'
                          ? 'success'
                          : r.scope === 'CATEGORY'
                            ? 'warning'
                            : 'neutral'
                      }
                    >
                      {r.scope}
                    </Badge>
                  </td>
                  <td className="text-ink-muted px-4 py-3">
                    {r.scope === 'GLOBAL' && 'All Products'}
                    {r.scope === 'CATEGORY' && (r.category?.name || r.categoryId)}
                    {r.scope === 'VENDOR' && (r.vendor?.storeName || r.vendorId)}
                  </td>
                  <td className="px-4 py-3 font-mono">{r.bps}</td>
                  <td className="text-ink px-4 py-3 font-medium">{(r.bps / 100).toFixed(2)}%</td>
                  <td className="text-ink-muted whitespace-nowrap px-4 py-3">
                    {new Date(r.effectiveFrom).toLocaleDateString()}
                    {r.effectiveTo && ` - ${new Date(r.effectiveTo).toLocaleDateString()}`}
                  </td>
                  <td className="flex gap-2 px-4 py-3">
                    <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(r)}>
                      <Edit2 className="text-ink-muted hover:text-ink h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (confirm('Are you sure you want to delete this rule?')) {
                          deleteMut.mutate(r.id);
                        }
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-red-500 hover:text-red-600" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-surface w-full max-w-md rounded-xl p-6 shadow-2xl">
            <h3 className="text-ink mb-4 text-lg font-bold">
              {editingRuleId ? 'Edit Commission Rule' : 'Create Commission Rule'}
            </h3>
            <div className="space-y-4">
              {!editingRuleId && (
                <div>
                  <label className="text-ink mb-1 block text-sm font-medium">Scope</label>
                  <select
                    className="border-border focus:border-brand-primary focus:ring-brand-primary/20 w-full rounded-md border p-2 text-sm outline-none transition-all focus:ring-2"
                    value={scope}
                    onChange={(e) => setScope(e.target.value as any)}
                  >
                    <option value="GLOBAL">Global</option>
                    <option value="CATEGORY">Category</option>
                    <option value="VENDOR">Vendor</option>
                  </select>
                </div>
              )}

              {scope === 'CATEGORY' && !editingRuleId && (
                <div>
                  <label className="text-ink mb-1 block text-sm font-medium">Category ID</label>
                  <Input
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                    placeholder="cat_..."
                  />
                </div>
              )}

              {scope === 'VENDOR' && !editingRuleId && (
                <div>
                  <label className="text-ink mb-1 block text-sm font-medium">Vendor ID</label>
                  <Input
                    value={vendorId}
                    onChange={(e) => setVendorId(e.target.value)}
                    placeholder="ven_..."
                  />
                </div>
              )}

              <div>
                <label className="text-ink mb-1 block text-sm font-medium">
                  Basis Points (BPS)
                </label>
                <Input
                  type="number"
                  min="0"
                  max="10000"
                  value={bps.toString()}
                  onChange={(e) => setBps(Number(e.target.value))}
                />
                <p className="text-ink-muted mt-1 text-xs">100 BPS = 1%</p>
              </div>

              {!editingRuleId && (
                <div>
                  <label className="text-ink mb-1 block text-sm font-medium">Effective From</label>
                  <Input
                    type="date"
                    value={effectiveFrom}
                    onChange={(e) => setEffectiveFrom(e.target.value)}
                  />
                </div>
              )}

              <div>
                <label className="text-ink mb-1 block text-sm font-medium">
                  Effective To (Optional)
                </label>
                <Input
                  type="date"
                  value={effectiveTo}
                  onChange={(e) => setEffectiveTo(e.target.value)}
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={createMut.isPending || updateMut.isPending}>
                {createMut.isPending || updateMut.isPending ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PlatformConfigTab() {
  const qc = useQueryClient();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const [isAdding, setIsAdding] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  const { data: settings, isLoading } = useQuery({
    queryKey: ['admin-global-settings'],
    queryFn: () => api.settings.list(),
  });

  const upsertMut = useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) =>
      api.settings.upsert(key, value),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-global-settings'] });
      setEditingKey(null);
      setIsAdding(false);
      setNewKey('');
      setNewValue('');
    },
  });

  const deleteMut = useMutation({
    mutationFn: (key: string) => api.settings.delete(key),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-global-settings'] });
    },
  });

  const handleSaveEdit = (key: string) => {
    try {
      const parsedValue = JSON.parse(editValue);
      upsertMut.mutate({ key, value: parsedValue });
    } catch {
      upsertMut.mutate({ key, value: editValue });
    }
  };

  const handleAddNew = () => {
    if (!newKey.trim() || !newValue.trim()) return;
    try {
      const parsedValue = JSON.parse(newValue);
      upsertMut.mutate({ key: newKey.trim(), value: parsedValue });
    } catch {
      upsertMut.mutate({ key: newKey.trim(), value: newValue });
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-ink text-lg font-semibold tracking-tight">Platform Configuration</h2>
        <Button onClick={() => setIsAdding(!isAdding)} size="sm">
          <Plus className="mr-2 h-4 w-4" /> Add Setting
        </Button>
      </div>

      {isAdding && (
        <div className="border-border bg-surface rounded-lg border p-4">
          <h3 className="text-ink mb-3 text-sm font-semibold">Add New Setting</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-ink mb-1 block text-xs font-medium">Key</label>
              <Input
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                placeholder="e.g. features_activation"
              />
            </div>
            <div>
              <label className="text-ink mb-1 block text-xs font-medium">
                Value (JSON or String)
              </label>
              <Input
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                placeholder='e.g. {"enabled": true}'
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setIsAdding(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleAddNew} disabled={upsertMut.isPending || !newKey}>
              {upsertMut.isPending ? 'Saving...' : 'Save Setting'}
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="grid min-h-[100px] place-items-center">
          <Spinner size="lg" />
        </div>
      ) : (settings ?? []).length === 0 ? (
        <EmptyState
          title="No platform settings"
          description="Global configuration settings will appear here once created."
        />
      ) : (
        <div className="border-border bg-surface overflow-hidden rounded-lg border">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-border bg-surface-subtle text-ink-muted border-b text-xs font-medium uppercase">
                <th className="px-4 py-3">Key</th>
                <th className="px-4 py-3">Value</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(settings ?? []).map((s) => (
                <tr
                  key={s.id}
                  className="border-border hover:bg-surface-subtle/50 border-b transition-colors last:border-0"
                >
                  <td className="px-4 py-3 font-mono text-xs">{s.key}</td>
                  <td className="px-4 py-3">
                    {editingKey === s.key ? (
                      <Input
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveEdit(s.key);
                          if (e.key === 'Escape') setEditingKey(null);
                        }}
                      />
                    ) : (
                      <pre className="text-ink max-w-md overflow-x-auto whitespace-pre-wrap text-xs">
                        {typeof s.value === 'object'
                          ? JSON.stringify(s.value, null, 2)
                          : String(s.value)}
                      </pre>
                    )}
                  </td>
                  <td className="flex justify-end gap-2 px-4 py-3">
                    {editingKey === s.key ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSaveEdit(s.key)}
                        disabled={upsertMut.isPending}
                      >
                        <Save className="text-brand-primary h-3.5 w-3.5" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingKey(s.key);
                          setEditValue(
                            typeof s.value === 'object' ? JSON.stringify(s.value) : String(s.value),
                          );
                        }}
                      >
                        <Edit2 className="text-ink-muted hover:text-ink h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Are you sure you want to delete ${s.key}?`)) {
                          deleteMut.mutate(s.key);
                        }
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-red-500 hover:text-red-600" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Which modules this admin sees. Not every platform runs every module — a
 * seller-only marketplace has no POS, a store with no blog has no Blog — and
 * an unused module in the sidebar is a daily distraction. Each switch saves
 * on its own and the sidebar changes at once. A few modules are locked: the
 * ones without which the marketplace, or this page, cannot be reached.
 */
function ModulesTab() {
  const queryClient = useQueryClient();
  const toggles = useModulesStore((st) => st.toggles);
  const setToggles = useModulesStore((st) => st.set);
  const loaded = useModulesStore((st) => st.loaded);
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (next: ModuleToggles) => api.cms.upsertSetting(MODULES_SETTING_KEY, next),
    onSuccess: (_, next) => {
      setToggles(next);
      void queryClient.invalidateQueries({ queryKey: ['website-setting', MODULES_SETTING_KEY] });
    },
  });

  const flip = async (id: string) => {
    const next: ModuleToggles = { ...(toggles ?? {}), [id]: !isModuleOn(toggles, id) };
    setPending(id);
    setError(null);
    try {
      await save.mutateAsync(next);
    } catch {
      setError('Could not save. Check your connection and try again.');
    } finally {
      setPending(null);
    }
  };

  const showAll = () => {
    setError(null);
    save.mutateAsync({}).catch(() => setError('Could not save. Try again.'));
  };

  if (!loaded) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const sections = Array.from(new Set(MODULES.map((m) => m.section)));
  const hiddenCount = MODULES.filter((m) => !isModuleOn(toggles, m.id)).length;

  return (
    <div className="space-y-6">
      <div className="border-border/60 bg-surface flex flex-wrap items-center justify-between gap-4 rounded-2xl border p-5">
        <div>
          <p className="text-ink text-[14px] font-bold">Sidebar modules</p>
          <p className="text-ink-muted mt-0.5 text-[13px] font-medium">
            Switch off what this platform does not use. It disappears from the sidebar for every
            admin. Nothing is deleted — switch it back on any time.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge tone={hiddenCount > 0 ? 'warning' : 'success'}>
            {hiddenCount === 0 ? 'All modules shown' : `${hiddenCount} hidden`}
          </Badge>
          {hiddenCount > 0 && (
            <Button variant="secondary" size="sm" onClick={showAll} disabled={save.isPending}>
              Show all
            </Button>
          )}
        </div>
      </div>

      {error && (
        <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] font-semibold text-red-700">
          {error}
        </p>
      )}

      {sections.map((section) => (
        <section key={section}>
          <h3 className="text-ink-muted mb-3 text-[11px] font-bold uppercase tracking-[0.14em]">
            {section}
          </h3>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {MODULES.filter((m) => m.section === section).map((m) => {
              const on = isModuleOn(toggles, m.id);
              const busy = pending === m.id;
              return (
                <div
                  key={m.id}
                  className={`border-border/60 flex items-start justify-between gap-4 rounded-2xl border p-4 shadow-sm ${
                    on ? 'bg-surface' : 'bg-surface-subtle'
                  }`}
                >
                  <div className="min-w-0">
                    <p className={`text-[14px] font-bold ${on ? 'text-ink' : 'text-ink-muted'}`}>
                      {m.label}
                    </p>
                    <p className="text-ink-muted mt-0.5 text-[12.5px] font-medium leading-relaxed">
                      {m.purpose}
                    </p>
                    {m.locked && (
                      <p className="text-ink-muted mt-2 flex items-center gap-1 text-[11.5px] font-semibold">
                        <Lock className="h-3 w-3" /> {m.locked}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={on}
                    aria-label={`${m.label}: ${on ? 'shown' : 'hidden'}`}
                    disabled={Boolean(m.locked) || busy}
                    onClick={() => void flip(m.id)}
                    className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                      on ? 'bg-brand-primary' : 'bg-border/40'
                    } ${m.locked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${
                        on ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<'commissions' | 'platform' | 'modules'>('commissions');

  return (
    <div>
      <PageHeader
        title="Settings Center"
        description="Commission rules, platform configuration, and which modules this admin shows."
      />

      <div className="border-border mb-6 flex gap-4 border-b">
        <button
          className={`pb-2 text-sm font-medium transition-colors ${
            activeTab === 'commissions'
              ? 'border-brand-primary text-brand-primary border-b-2'
              : 'text-ink-muted hover:text-ink border-b-2 border-transparent'
          }`}
          onClick={() => setActiveTab('commissions')}
        >
          <div className="flex items-center gap-2">
            <Settings2 className="h-4 w-4" />
            Commission Rules
          </div>
        </button>
        <button
          className={`pb-2 text-sm font-medium transition-colors ${
            activeTab === 'platform'
              ? 'border-brand-primary text-brand-primary border-b-2'
              : 'text-ink-muted hover:text-ink border-b-2 border-transparent'
          }`}
          onClick={() => setActiveTab('platform')}
        >
          <div className="flex items-center gap-2">
            <Settings2 className="h-4 w-4" />
            Platform Config
          </div>
        </button>
        <button
          className={`pb-2 text-sm font-medium transition-colors ${
            activeTab === 'modules'
              ? 'border-brand-primary text-brand-primary border-b-2'
              : 'text-ink-muted hover:text-ink border-b-2 border-transparent'
          }`}
          onClick={() => setActiveTab('modules')}
        >
          <div className="flex items-center gap-2">
            <LayoutGrid className="h-4 w-4" />
            Modules
          </div>
        </button>
      </div>

      {activeTab === 'commissions' && <CommissionRulesTab />}
      {activeTab === 'platform' && <PlatformConfigTab />}
      {activeTab === 'modules' && <ModulesTab />}
    </div>
  );
}
